import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { NEW_CHAT_PATH } from 'src/content/ChatV2/constants';
import useMutation from 'src/hooks/useMutation';
import { useCurrentPathname } from 'src/layouts/SidebarV2/hooks/useCurrentPathname';
import { isChatOpenAt } from 'src/layouts/SidebarV2/utils';
import { setChats } from 'src/redux/slices/chat';
import type { RootState } from 'src/redux/store';
import { useDispatch, useSelector } from 'src/redux/store';
import { useDeleteChatMutation, useRenameChatMutation } from 'src/services/api';
import type { DataApiInputParams } from 'src/types/api';
import type { ChatData } from 'src/types/chat';
import { ErrorContext } from 'src/utils/errorMappings';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';

export interface UseChatActions {
  renameChat: (id: number, title: string) => Promise<void>;
  deleteChat: (id: number) => Promise<void>;
  isDeleting: boolean;
}

export const useChatActions = (): UseChatActions => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentPathname = useCurrentPathname();
  const chats = useSelector((state: RootState) => state.chat.chats) as ChatData[];

  const renameMutation = useMutation<DataApiInputParams, unknown>({
    api: useRenameChatMutation,
    errorContext: ErrorContext.CHAT
  });
  const renameChatMutation = renameMutation[0];

  const deleteMutation = useMutation<DataApiInputParams, unknown>({
    api: useDeleteChatMutation,
    errorContext: ErrorContext.CHAT
  });
  const deleteChatMutation = deleteMutation[0];
  const isDeleting = Boolean(deleteMutation[1].isLoading);

  const renameChat = useCallback(
    async (id: number, title: string) => {
      await renameChatMutation({
        params: { params: { id, topic: title } },
        fallbackMsg: T.chatRenameFailed,
        successMsg: T.chatRenameSuccess
      });
      dispatch(
        setChats(
          chats.map((chat: ChatData) =>
            chat.id === id ? { ...chat, subject: title } : chat
          )
        )
      );
    },
    [renameChatMutation, dispatch, chats]
  );

  const deleteChat = useCallback(
    async (id: number) => {
      await deleteChatMutation({
        params: { params: { id } },
        fallbackMsg: T.chatDeleteFailed,
        successMsg: T.chatDeleteSuccess,
        successCallback: () => {
          dispatch(
            setChats(chats.filter((chat: ChatData) => chat.id !== id))
          );
          if (isChatOpenAt(currentPathname, id)) {
            navigate(NEW_CHAT_PATH, { replace: true, state: null });
          }
        }
      });
    },
    [deleteChatMutation, dispatch, chats, navigate, currentPathname]
  );

  return { renameChat, deleteChat, isDeleting };
};
